import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Activity, TrendingUp, TrendingDown, AlertCircle, CheckCircle, RefreshCw, Clock, Wifi, WifiOff } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import SEOHead from '../components/SEOHead';
import { 
  getRecentPeg, 
  getStatusFromBps, 
  getStatusConfig, 
  formatBps, 
  formatTime, 
  formatDateTime 
} from '../services/emergentApiClient';
import { getUserFriendlyError, ErrorDisplay } from '../utils/errorMessages';
import { MetricTooltip } from '../components/ui/tooltip';

const PegStatusPage = () => {
  const [pegData, setPegData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fonction pour récupérer les données
  const fetchPegData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }
      
      setError(null);
      
      // Récupérer les données via notre client API
      const data = await getRecentPeg({ 
        asset: "USDC", 
        limit: 50 
      });
      
      setPegData(data);
      setLastUpdate(new Date());
      
    } catch (err) {
      console.error('Erreur lors de la récupération des données peg:', err);
      setError(getUserFriendlyError(err));
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Chargement initial
  useEffect(() => {
    fetchPegData();
  }, [fetchPegData]);

  // Auto-refresh toutes les 60 secondes
  useEffect(() => {
    const interval = setInterval(() => {
      fetchPegData(true); // isRefresh = true
    }, 60000);
    
    return () => clearInterval(interval);
  }, [fetchPegData]);

  // Préparation des données pour le graphique
  const chartData = pegData?.recent_events?.map((event, index) => ({
    index: pegData.recent_events.length - index, // Inverser pour chronologie
    time: formatTime(event.observed_at),
    fullTime: event.observed_at,
    deviation: event.price_dev_bps,
    price: event.price_usd,
    status: getStatusFromBps(event.price_dev_bps)
  })).reverse() || [];

  // Custom tooltip pour le graphique
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const statusConfig = getStatusConfig(data.status);
      
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg">
          <p className="text-sm font-medium">{formatDateTime(data.fullTime)}</p>
          <p className={`text-sm ${statusConfig.color}`}>
            {statusConfig.icon} {formatBps(data.deviation)} • {statusConfig.label}
          </p>
          <p className="text-xs text-gray-500">Prix: ${data.price.toFixed(4)}</p>
        </div>
      );
    }
    return null;
  };

  // États de chargement
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Status Peg USDC</h1>
            <div className="flex items-center justify-center space-x-2">
              <Activity className="w-5 h-5 animate-spin text-blue-600" />
              <span className="text-gray-600">Chargement des données...</span>
            </div>
          </div>
          
          {/* Squelettes de chargement */}
          <div className="space-y-6">
            <Card>
              <CardContent className="p-6">
                <div className="animate-pulse space-y-4">
                  <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                  <div className="grid grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => (
                      <div key={i}>
                        <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                        <div className="h-6 bg-gray-200 rounded"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
                  <div className="h-64 bg-gray-200 rounded"></div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  const currentStatus = pegData?.current_status;
  const statusFromBps = currentStatus ? getStatusFromBps(currentStatus.price_dev_bps) : 'stable';
  const statusConfig = getStatusConfig(statusFromBps);

  return (
    <>
      <SEOHead 
        title="Status Peg USDC Temps Réel - Monitoring Déviation | StableYield"
        description="Monitoring temps réel du peg USDC/USD avec historique déviations, alertes automatiques et métriques stabilité. Graphiques interactifs, données CoinGecko, mise à jour 60s."
        keywords="USDC peg, monitoring temps réel, déviation basis points, stablecoin stabilité, peg depeg alert, USDC price deviation, stablecoin monitoring"
        url="https://stableyield.com/peg-status"
        image="https://stableyield.com/images/peg-status-preview.jpg"
      />
      
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="container mx-auto px-4 py-8">
          
          {/* Header avec status de connexion */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-2 mb-4">
              {error ? (
                <WifiOff className="w-5 h-5 text-red-500" />
              ) : (
                <Wifi className="w-5 h-5 text-green-500" />
              )}
              <h1 className="text-4xl font-bold text-gray-900">
                Status Peg USDC
              </h1>
              {isRefreshing && (
                <RefreshCw className="w-5 h-5 animate-spin text-blue-600" />
              )}
            </div>
            <p className="text-lg text-gray-600">
              Monitoring temps réel • Mise à jour toutes les 60s
            </p>
            {lastUpdate && (
              <p className="text-sm text-gray-500 mt-2">
                <Clock className="w-4 h-4 inline mr-1" />
                Dernière mise à jour: {formatDateTime(lastUpdate.toISOString())}
              </p>
            )}
          </div>

          {/* Bannière d'erreur */}
          {error && (
            <div className="mb-6">
              <ErrorDisplay 
                error={{ message: error }}
                onRetry={() => fetchPegData()}
              />
            </div>
          )}

          {/* A. Statut actuel */}
          {currentStatus && (
            <Card className={`mb-8 border-2 ${statusConfig.borderColor}`}>
              <CardHeader className={statusConfig.bgColor}>
                <CardTitle className="flex items-center space-x-2">
                  <span className="text-2xl">{statusConfig.icon}</span>
                  <span>Status Actuel - {currentStatus.asset}</span>
                  <Badge className={`${statusConfig.color} ${statusConfig.bgColor}`}>
                    {statusConfig.label}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  
                  {/* Prix */}
                  <div className="text-center">
                    <p className="text-sm text-gray-500 mb-1">Prix USD</p>
                    <p className="text-3xl font-bold text-gray-900">
                      ${currentStatus.price_usd.toFixed(4)}
                    </p>
                  </div>

                  {/* Déviation avec tooltip explicatif */}
                  <div className="text-center">
                    <MetricTooltip 
                      metric="BPS (Basis Points)"
                      explanation="1 bps = 0,01%. Mesure la déviation du prix par rapport à 1,00 USD"
                    >
                      <p className="text-sm text-gray-500 mb-1">Déviation</p>
                    </MetricTooltip>
                    <div className="flex items-center justify-center space-x-1">
                      {currentStatus.price_dev_bps > 0 ? (
                        <TrendingUp className="w-5 h-5 text-red-500" />
                      ) : currentStatus.price_dev_bps < 0 ? (
                        <TrendingDown className="w-5 h-5 text-blue-500" />
                      ) : (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      )}
                      <span className={`text-2xl font-bold ${statusConfig.color}`}>
                        {formatBps(currentStatus.price_dev_bps)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {Math.abs(currentStatus.price_dev_bps) === 0 && "Prix parfait 1:1"}
                      {Math.abs(currentStatus.price_dev_bps) > 0 && Math.abs(currentStatus.price_dev_bps) < 25 && "Écart minimal"}
                      {Math.abs(currentStatus.price_dev_bps) >= 25 && Math.abs(currentStatus.price_dev_bps) < 50 && "Écart normal"}
                      {Math.abs(currentStatus.price_dev_bps) >= 50 && Math.abs(currentStatus.price_dev_bps) < 200 && "Surveillance"}
                      {Math.abs(currentStatus.price_dev_bps) >= 200 && "Alerte dépeg"}
                    </p>
                  </div>

                  {/* Status avec métrique */}
                  <div className="text-center">
                    <p className="text-sm text-gray-500 mb-1">Classification</p>
                    <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusConfig.color} ${statusConfig.bgColor}`}>
                      {statusConfig.label}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {Math.abs(currentStatus.price_dev_bps) < 50 && "Plage normale"}
                      {Math.abs(currentStatus.price_dev_bps) >= 50 && Math.abs(currentStatus.price_dev_bps) < 200 && "Surveillance renforcée"}
                      {Math.abs(currentStatus.price_dev_bps) >= 200 && "Alerte dépeg"}
                    </p>
                  </div>

                  {/* Source et heure */}
                  <div className="text-center">
                    <p className="text-sm text-gray-500 mb-1">Source</p>
                    <p className="text-lg font-medium text-gray-700">
                      {currentStatus.exchange}
                    </p>
                    <p className="text-sm text-gray-500">
                      {formatTime(currentStatus.observed_at)}
                    </p>
                  </div>

                </div>
              </CardContent>
            </Card>
          )}

          {/* B. Courbe avec zones colorées */}
          {chartData.length > 0 && (
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Évolution des Déviations (50 derniers points)</CardTitle>
                <p className="text-sm text-gray-600">
                  Zones: 🟢 Stable (±50 bps) • 🟡 Surveillance (±200 bps) • 🔴 Alerte (&gt;200 bps)
                </p>
              </CardHeader>
              <CardContent>
                <div style={{ width: '100%', height: 350 }}>
                  <ResponsiveContainer>
                    <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                      
                      {/* Zones colorées */}
                      <defs>
                        <linearGradient id="alertZoneTop" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#fee2e2" stopOpacity={0.3}/>
                          <stop offset="100%" stopColor="#fef2f2" stopOpacity={0.1}/>
                        </linearGradient>
                        <linearGradient id="alertZoneBottom" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#fef2f2" stopOpacity={0.1}/>
                          <stop offset="100%" stopColor="#fee2e2" stopOpacity={0.3}/>
                        </linearGradient>
                        <linearGradient id="warningZone" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#fef3c7" stopOpacity={0.2}/>
                          <stop offset="100%" stopColor="#fffbeb" stopOpacity={0.1}/>
                        </linearGradient>
                        <linearGradient id="stableZone" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#dcfce7" stopOpacity={0.3}/>
                          <stop offset="100%" stopColor="#f0fdf4" stopOpacity={0.1}/>
                        </linearGradient>
                      </defs>
                      
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      
                      {/* Lignes de référence pour les zones */}
                      <ReferenceLine y={200} stroke="#ef4444" strokeDasharray="2 2" />
                      <ReferenceLine y={50} stroke="#f59e0b" strokeDasharray="2 2" />
                      <ReferenceLine y={0} stroke="#10b981" strokeWidth={2} />
                      <ReferenceLine y={-50} stroke="#f59e0b" strokeDasharray="2 2" />
                      <ReferenceLine y={-200} stroke="#ef4444" strokeDasharray="2 2" />
                      
                      <XAxis 
                        dataKey="time" 
                        tick={{ fontSize: 11 }}
                        interval="preserveStartEnd"
                      />
                      <YAxis 
                        label={{ value: 'Déviation (bps)', angle: -90, position: 'insideLeft' }}
                        tick={{ fontSize: 11 }}
                        domain={['dataMin - 10', 'dataMax + 10']}
                      />
                      
                      <Tooltip content={<CustomTooltip />} />
                      
                      <Line 
                        type="monotone" 
                        dataKey="deviation" 
                        stroke="#2563eb" 
                        strokeWidth={2}
                        dot={(props) => {
                          const { cx, cy, payload } = props;
                          const status = payload.status;
                          const config = getStatusConfig(status);
                          const color = status === 'stable' ? '#10b981' : status === 'warning' ? '#f59e0b' : '#ef4444';
                          
                          return (
                            <circle 
                              cx={cx} 
                              cy={cy} 
                              r={3} 
                              fill={color} 
                              stroke="white" 
                              strokeWidth={2}
                            />
                          );
                        }}
                        connectNulls
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* C. Table "Derniers événements" */}
          {pegData?.recent_events && (
            <Card>
              <CardHeader>
                <CardTitle>Derniers Événements</CardTitle>
                <p className="text-sm text-gray-600">
                  Historique des {pegData.recent_events.length} dernières mesures
                </p>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-gray-200">
                        <th className="text-left p-3 font-semibold">Date</th>
                        <th className="text-center p-3 font-semibold">Exchange</th>
                        <th className="text-right p-3 font-semibold">Prix USD</th>
                        <th className="text-right p-3 font-semibold">Déviation</th>
                        <th className="text-center p-3 font-semibold">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pegData.recent_events.slice(0, 20).map((event, index) => {
                        const eventStatus = getStatusFromBps(event.price_dev_bps);
                        const eventStatusConfig = getStatusConfig(eventStatus);
                        
                        return (
                          <tr key={index} className="border-b hover:bg-gray-50 transition-colors">
                            <td className="p-3">
                              <div>
                                <div className="font-medium">{formatDateTime(event.observed_at)}</div>
                              </div>
                            </td>
                            <td className="p-3 text-center">
                              <span className="text-gray-600">CoinGecko</span>
                            </td>
                            <td className="p-3 text-right font-mono">
                              ${event.price_usd.toFixed(4)}
                            </td>
                            <td className={`p-3 text-right font-mono font-semibold ${eventStatusConfig.color}`}>
                              {formatBps(event.price_dev_bps)}
                            </td>
                            <td className="p-3 text-center">
                              <Badge className={`${eventStatusConfig.color} ${eventStatusConfig.bgColor} text-xs`}>
                                {eventStatusConfig.icon} {eventStatusConfig.label}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                
                {pegData.recent_events.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>Aucun événement récent disponible</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

        </div>
      </div>
    </>
  );
};

export default PegStatusPage;